import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { X, Pencil } from "lucide-react";
import { api, fmtUSD, fmtPrice, fmtDate } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import CloseTradeDialog from "@/components/CloseTradeDialog";
import EditTradeDialog from "@/components/EditTradeDialog";

export default function OpenTrades() {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/trades", { params: { status: "open" } });
      setTrades(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [load]);

  const quickClose = async (id) => {
    if (!window.confirm("Close this trade at current market price?")) return;
    try {
      await api.post(`/admin/trades/${id}/close`, {});
      toast.success("Trade closed at market");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const totalPnL = trades.reduce((s, t) => s + (t.pnl || 0), 0);

  return (
    <div className="space-y-6" data-testid="open-trades-page">
      <div className="grid grid-cols-3 gap-px bg-border">
        <div className="bg-card p-5"><div className="label-eyebrow mb-1">Open Positions</div><div className="font-mono-num text-2xl font-semibold">{trades.length}</div></div>
        <div className="bg-card p-5"><div className="label-eyebrow mb-1">Total Volume</div><div className="font-mono-num text-2xl font-semibold">{trades.reduce((s,t)=>s+t.lots,0).toFixed(2)} lots</div></div>
        <div className="bg-card p-5"><div className="label-eyebrow mb-1">Floating P&amp;L</div><div className={`font-mono-num text-2xl font-semibold ${totalPnL >= 0 ? "text-profit" : "text-loss"}`} data-testid="open-floating-pnl">{fmtUSD(totalPnL, { signed: true })}</div></div>
      </div>

      <div className="border border-subtle bg-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-surface-hover border-subtle">
              <TableHead>Client</TableHead>
              <TableHead>Instrument</TableHead>
              <TableHead>Side</TableHead>
              <TableHead className="text-right">Lots</TableHead>
              <TableHead className="text-right">Entry</TableHead>
              <TableHead className="text-right">Current</TableHead>
              <TableHead className="text-right">SL / TP</TableHead>
              <TableHead className="text-right">P&amp;L (USD)</TableHead>
              <TableHead>Opened</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={10} className="text-center py-12 text-muted-foreground">Loading…</TableCell></TableRow>}
            {!loading && trades.length === 0 && <TableRow><TableCell colSpan={10} className="text-center py-12 text-muted-foreground">No open trades</TableCell></TableRow>}
            {trades.map((t) => (
              <TableRow key={t.id} className="border-subtle hover:bg-surface-hover" data-testid={`open-trade-${t.id}`}>
                <TableCell className="text-sm">{t.user_name || t.user_id}</TableCell>
                <TableCell className="font-semibold">{t.instrument}</TableCell>
                <TableCell>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded ${t.side === "buy" ? "bg-profit/15 text-profit" : "bg-loss/15 text-loss"}`}>
                    {t.side.toUpperCase()}
                  </span>
                </TableCell>
                <TableCell className="text-right font-mono-num">{t.lots.toFixed(2)}</TableCell>
                <TableCell className="text-right font-mono-num">{fmtPrice(t.instrument, t.entry_price)}</TableCell>
                <TableCell className="text-right font-mono-num">{fmtPrice(t.instrument, t.current_price)}</TableCell>
                <TableCell className="text-right font-mono-num text-xs text-muted-foreground">
                  {t.stop_loss ? fmtPrice(t.instrument, t.stop_loss) : "—"} / {t.take_profit ? fmtPrice(t.instrument, t.take_profit) : "—"}
                </TableCell>
                <TableCell className={`text-right font-mono-num font-semibold ${t.pnl >= 0 ? "text-profit" : "text-loss"}`} data-testid={`pnl-${t.id}`}>
                  {fmtUSD(t.pnl, { signed: true })}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{fmtDate(t.opened_at)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-1 justify-end">
                    <button onClick={() => setEditing(t)} className="p-1.5 hover:bg-surface-hover rounded" data-testid={`edit-open-${t.id}`} title="Edit trade">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => setClosing(t)} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-primary/10 text-primary hover:bg-primary hover:text-white transition-colors rounded font-medium" data-testid={`close-custom-${t.id}`}>
                      Close…
                    </button>
                    <button onClick={() => quickClose(t.id)} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-loss/10 text-loss hover:bg-loss hover:text-white transition-colors rounded font-medium" data-testid={`close-trade-${t.id}`} title="Close at current market">
                      <X className="w-3 h-3" /> Market
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <CloseTradeDialog
        trade={closing}
        open={!!closing}
        onOpenChange={(o) => !o && setClosing(null)}
        onClosed={load}
      />

      <EditTradeDialog
        trade={editing}
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        onSaved={load}
        onClose={(t) => setClosing(t)}
      />
    </div>
  );
}
